import { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/ui/modal.tsx";
import { Button } from "../../components/ui/button.tsx";
import { SearchBox } from "../../components/ui/searchBox.tsx";
import { AsyncBoundary, type AsyncState } from "../../components/ui/asyncBoundary.tsx";
import { Skeleton } from "../../components/ui/skeleton.tsx";
import { Alert } from "../../components/ui/alert.tsx";
import { useSession } from "../../hooks/useSession.ts";
import { buildProductImageThumbCandidates } from "../../util/images.ts";
import {
  listProductImages, PRODUCT_IMAGE_BROWSE_ROWS, PRODUCT_IMAGE_SEARCH_ROWS,
  PRODUCT_IMAGE_CANDIDATE_FACTOR, PRODUCT_IMAGE_MAX_CANDIDATES, type ProductImageOption,
} from "../../api/productImages.ts";
import { firstLoadingImage, pickWithPreview } from "../../util/imagePreview.ts";
import styles from "./chooseProductImageModal.module.scss";

/** A listed image together with the URL it was found to display from. */
interface PreviewedImage {
  option: ProductImageOption;
  src: string;
}

export interface ChooseProductImageModalProps {
  /** Operator folder to list — the target product's OperatorID (0 = base folder). */
  operatorId: number;
  /** The product's current image name, marked in the grid. */
  currentImageName?: string;
  /** Label for the primary button (e.g. "Use image", "Use after create"). */
  chooseLabel?: string;
  onClose: () => void;
  /**
   * Called with the picked image. Resolve `null` on success (the caller closes the modal),
   * or an error message to show in the modal and keep it open.
   */
  onChoose: (option: ProductImageOption) => Promise<string | null> | string | null;
}

/**
 * "Choose existing image" for the product editor: a searchable thumbnail grid of the product
 * images already in use in the target product's operator folder (`WebListProductImages`), so an
 * image can be reused instead of uploaded again. Upload stays the way to add a new file.
 */
export function ChooseProductImageModal({ operatorId, currentImageName, chooseLabel = "Use image", onClose, onChoose }: ChooseProductImageModalProps) {
  const { cache } = useSession();
  const [search, setSearch] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const [state, setState] = useState<AsyncState<PreviewedImage[]>>({ status: "loading" });
  /** Still checking further candidates for a preview after the first ones were shown. */
  const [checkingMore, setCheckingMore] = useState(false);
  const [selected, setSelected] = useState<ProductImageOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dedupe the StrictMode double-mount by key; a sequence number drops answers to a search the
  // user has already typed past (no `cancelled` flag — see AGENTS.md "Loading data in useEffect").
  const lastKeyRef = useRef("");
  const seqRef = useRef(0);
  useEffect(() => {
    const key = `${operatorId}|${search}|${reloadNonce}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    const seq = ++seqRef.current;
    setState({ status: "loading" });
    setCheckingMore(false);
    const want = search ? PRODUCT_IMAGE_SEARCH_ROWS : PRODUCT_IMAGE_BROWSE_ROWS;
    const isStale = () => seq !== seqRef.current;
    // Many stored names have no file on the image host any more. Ask for several times as many
    // candidates as are shown, load them quietly, and show only those that display — so the
    // grid never fills with "No preview" tiles. Tiles appear as each batch is checked.
    listProductImages(operatorId, search, Math.min(want * PRODUCT_IMAGE_CANDIDATE_FACTOR, PRODUCT_IMAGE_MAX_CANDIDATES))
      .then(async res => {
        if (isStale()) return;
        if (res.bError) { setState({ status: "error", error: new Error(res.sMessage || "Could not load images") }); return; }
        const candidates = res.oData ?? [];
        setCheckingMore(true);
        const found = await pickWithPreview(
          candidates,
          o => firstLoadingImage(buildProductImageThumbCandidates(cache, o.ImageName, o.OperatorID)),
          want,
          { isStale, onProgress: rows => setState({ status: "ready", data: rows.map(r => ({ option: r.item, src: r.src })) }) },
        );
        if (isStale()) return;
        setCheckingMore(false);
        if (found.length === 0) setState({ status: "empty" });
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        setState({ status: "error", error: err });
      });
  }, [operatorId, search, reloadNonce]);

  const handleChoose = async (option: ProductImageOption | null = selected) => {
    if (!option || saving) return;
    setSaving(true);
    setError(null);
    try {
      const problem = await onChoose(option);
      if (problem) setError(problem);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not use the image");
    } finally {
      setSaving(false);
    }
  };

  const isSelected = (o: ProductImageOption) =>
    selected != null && selected.ImageName === o.ImageName && selected.OperatorID === o.OperatorID;

  return (
    <Modal open onClose={onClose} wide>
      <ModalHeader title="Choose existing image" onClose={onClose} />
      <ModalBody>
        <div className={styles.toolbar}>
          <SearchBox
            placeholder="Search by image or product name"
            delayMs={300}
            onDebouncedChange={v => setSearch(v.trim())}
          />
          <span className={styles.hint}>
            {operatorId > 0
              ? `Images used by operator ${operatorId}'s products, plus shared images from admin-level products. Newest first.`
              : "Shared images from admin-level products. Newest first."}
          </span>
        </div>
        {error && <Alert variant="error" style={{ marginBottom: "var(--space-3)" }}>{error}</Alert>}
        <AsyncBoundary
          state={state}
          onRetry={() => setReloadNonce(n => n + 1)}
          loading={
            <ul className={styles.grid} aria-busy="true" aria-label="Loading images">
              {Array.from({ length: 12 }, (_, i) => (
                <li key={i}><Skeleton variant="rect" className={styles.skeletonTile} /></li>
              ))}
            </ul>
          }
          empty={{
            title: search ? "No images match" : "No images yet",
            description: search
              ? "No image with a preview matches. Try another part of the image or product name."
              : "No product here has an image with a preview. Use Upload Image instead.",
          }}
        >
          {rows => (
            <>
              <p className={styles.hint} style={{ marginBottom: "var(--space-2)" }} aria-live="polite">
                {checkingMore
                  ? `Showing ${rows.length} so far — looking for more images with a preview…`
                  : search
                    ? `Showing ${rows.length} ${rows.length === 1 ? "match" : "matches"} with a preview. Type more of the name to narrow it down.`
                    : `Showing the ${rows.length} newest images with a preview. Search by image or product name to find others.`}
              </p>
              <ul className={styles.grid} aria-label="Existing product images">
                {rows.map(({ option: o, src }) => {
                  const on = isSelected(o);
                  const isCurrent = !!currentImageName && currentImageName === o.ImageName;
                  const usedBy = `Used by ${o.ProductCount} product${o.ProductCount === 1 ? "" : "s"}`;
                  return (
                    <li key={`${o.OperatorID}/${o.ImageName}`}>
                      <button
                        type="button"
                        className={`${styles.tile}${on ? ` ${styles.tileSelected}` : ""}`}
                        aria-pressed={on}
                        title={o.ImageName}
                        onClick={() => { setSelected(o); setError(null); }}
                        onDoubleClick={() => { setSelected(o); void handleChoose(o); }}
                      >
                        <span className={styles.thumbBox}>
                          <Thumb urls={[src]} alt={o.ImageName} />
                        </span>
                        <span className={styles.name}>{o.ImageName}</span>
                        {operatorId > 0 && o.OperatorID === 0 && (
                          <span className={styles.sharedTag} title="From an admin-level product — copied into this operator's folder when chosen">Shared</span>
                        )}
                        <span className={styles.meta} title={o.ExampleProductName ?? undefined}>
                          {o.ExampleProductName ? `${usedBy} · ${o.ExampleProductName}` : usedBy}
                        </span>
                        {(on || isCurrent) && (
                          <span className={styles.selectedMark}>
                            {on ? "Selected" : "Current image"}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </AsyncBoundary>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={() => void handleChoose()} disabled={!selected} loading={saving}>
          {chooseLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** Tries each candidate URL in turn; a placeholder once all have failed. */
function Thumb({ urls, alt }: { urls: string[]; alt: string }) {
  const [index, setIndex] = useState(0);
  const key = urls.join("|");
  const prevKey = useRef(key);
  if (prevKey.current !== key) { prevKey.current = key; if (index !== 0) setIndex(0); }

  if (index >= urls.length) return <span className={styles.noPreview}>No preview</span>;
  return (
    <img
      className={styles.thumb}
      src={urls[index]}
      alt={alt}
      loading="lazy"
      onError={() => setIndex(i => i + 1)}
    />
  );
}
